import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, map } from 'rxjs';
import { environment } from '@htx/environment';
import {
  DeploymentState,
  ManagedApp,
  TenantStatus,
  UseCaseApp,
  USE_CASE_APP_METADATA
} from './app-management.model';

@Injectable({
  providedIn: 'root'
})
export class AppManagementService {
  private httpClient = inject(HttpClient);

  private deploymentUrl(organizationId: string): string {
    return `${environment.backendUrl}/organizations/${organizationId}/deployment`;
  }

  provisionConnector(organizationId: string): Observable<TenantStatus> {
    return this.httpClient.post<TenantStatus>(this.deploymentUrl(organizationId), {});
  }

  getDeploymentStatus(organizationId: string): Observable<TenantStatus> {
    return this.httpClient.get<TenantStatus>(this.deploymentUrl(organizationId));
  }

  getApps(organizationId: string): Observable<ManagedApp[]> {
    return this.httpClient
      .get<Array<{ id: UseCaseApp; appUrl: string | null; online: boolean; deploymentState: DeploymentState }>>(
        `${this.deploymentUrl(organizationId)}/apps`
      )
      .pipe(
        map((apps) =>
          apps
            .filter((app) => USE_CASE_APP_METADATA[app.id] != null)
            .map((app) => ({
              id: app.id,
              ...USE_CASE_APP_METADATA[app.id],
              deploymentState: app.deploymentState,
              appUrl: app.appUrl,
              online: app.online
            }))
        )
      );
  }

  enableApp(organizationId: string, appId: UseCaseApp): Observable<void> {
    const metadata = USE_CASE_APP_METADATA[appId];
    const path = metadata?.useAppEndpoint ? `apps/${appId}` : `use-cases/${appId}`;
    return this.httpClient.put<void>(`${this.deploymentUrl(organizationId)}/${path}`, {});
  }

  disableApp(organizationId: string, appId: UseCaseApp): Observable<void> {
    const metadata = USE_CASE_APP_METADATA[appId];
    const path = metadata?.useAppEndpoint ? `apps/${appId}` : `use-cases/${appId}`;
    return this.httpClient.delete<void>(`${this.deploymentUrl(organizationId)}/${path}`);
  }
}
